// Health Break — i18n strings (EN + TR)

export const LANGS = {
  en: { code: "en", label: "EN", name: "English" },
  tr: { code: "tr", label: "TR", name: "Türkçe" },
};

export const translations = {
  en: {
    brand: {
      name: "Health Break",
      kicker: "Move · Pause · Repeat",
      tagline: "One minute at a time.",
    },
    nav: {
      features: "Features",
      how: "How it works",
      preview: "App preview",
      reviews: "Reviews",
      faq: "FAQ",
      cta: "Get the app",
      menuOpen: "Open menu",
      menuClose: "Close menu",
      lightAria: "Switch to light mode",
      darkAria: "Switch to dark mode",
    },
    hero: {
      eyebrow: "Built for desks, not gyms",
      h1Pre: "Spaced 1-minute breaks for",
      h1Highlight: "a healthier body.",
      h1Post: "",
      sub: "Health Break is the calm wellness app for people who sit all day. A handful of 1-minute guided breaks across your workday, smart reminders that respect your focus, and a weekly rhythm that prevents neck, back and wrist pain.",
      ctaPrimary: "Download App",
      ctaSecondary: "See how it works",
      trust: "Built with input from physiotherapists. For people who work at a desk all day.",
      shotAlt: "Health Break — Cervical Rotation exercise screen",
    },
    problem: {
      kicker: "The desk problem",
      h2Pre: "Sitting wasn’t supposed to feel",
      h2Highlight: "this heavy.",
      lead: "Most desk workers spend 8+ hours frozen in one position. The body asks for small movements all day — most of us only listen when something already hurts.",
      withoutTitle: "Without breaks",
      withTitle: "With Health Break",
      withoutItems: [
        "Neck and shoulders tight by lunchtime",
        "Lower back pain that doesn’t switch off",
        "Wrists buzzing after long typing sessions",
        "Forgetting to move until it actually hurts",
      ],
      withItems: [
        "60-second resets you’ll actually do",
        "Targeted relief for desk hotspots",
        "Reminders that respect your focus",
        "A weekly rhythm that builds a real habit",
      ],
    },
    features: {
      kicker: "What's inside",
      h2Pre: "Tiny rituals,",
      h2Highlight: "designed for desks.",
      lead: "Six gentle features that turn long sitting hours into a calmer, healthier daily rhythm.",
      items: [
        {
          id: "one-minute",
          title: "1-minute breaks, all day long",
          description:
            "Short guided routines triggered at the right moments — between meetings, after long focus blocks, before that 3pm dip.",
          icon: "Timer",
          span: "md:col-span-7",
          accent: "emerald",
        },
        {
          id: "targeted-areas",
          title: "Targeted for desk pain",
          description:
            "Neck, upper back, lower back and wrists — the four hotspots that screen time hurts most.",
          icon: "PersonSimpleTaiChi",
          span: "md:col-span-5",
          accent: "warm",
        },
        {
          id: "smart-reminders",
          title: "Reminders that respect your day",
          description:
            "Pulses tuned to your real work hours and focus blocks. Quiet during deep work, gentle when your body needs a reset.",
          icon: "BellSimpleRinging",
          span: "md:col-span-5",
          accent: "warm",
        },
        {
          id: "weekly-rhythm",
          title: "Desk time + movement, in one rhythm",
          description:
            "We quietly track how long you've been working and pair it with your sessions, so a calm weekly picture appears on its own.",
          icon: "ChartLineUp",
          span: "md:col-span-7",
          accent: "emerald",
        },
        {
          id: "friendly-onboarding",
          title: "Sign in your way",
          description:
            "Apple, Google, or email + password. A 30-second onboarding picks the body parts that feel tired — and we take it from there.",
          icon: "SignIn",
          span: "md:col-span-6",
          accent: "neutral",
        },
        {
          id: "habit-insights",
          title: "Insights that build a real habit",
          description:
            "Streaks, calm wins, and gentle nudges that turn 60 seconds into the best part of your day — not another guilt feed.",
          icon: "Medal",
          span: "md:col-span-6",
          accent: "neutral",
        },
      ],
    },
    how: {
      kicker: "How it works",
      h2Pre: "From stiff to settled in",
      h2Highlight: "three small steps.",
      stepLabel: "Step",
      steps: [
        {
          number: "01",
          title: "Set your preferences",
          description:
            "Tell us your work hours and the body parts that get tight. Sign in with Apple, Google or email — takes 30 seconds.",
        },
        {
          number: "02",
          title: "Get gentle reminders",
          description:
            "We nudge you at the right moments based on how long you've been working — never when you're deep in flow.",
        },
        {
          number: "03",
          title: "Move for 60 seconds",
          description:
            "Follow a guided micro-exercise, log how it felt, and watch your week build into a calm rhythm.",
        },
      ],
    },
    preview: {
      kicker: "Inside the app",
      h2: "A calm space, hidden inside your busiest day.",
      lead: "Health Break is designed to disappear — until you need it. Soft typography, honest illustrations, and almost no notifications you don’t ask for.",
      authTitle: "Sign in your way",
      authNote:
        "Continue with the option you already trust. Switch anytime in settings.",
      authApple: "Apple",
      authGoogle: "Google",
      authEmail: "Email",
      highlights: [
        {
          title: "Today, with intention",
          description:
            "A quiet morning view that shows the small breaks shaped around your day — not a feed, not a guilt trip.",
          bullets: [
            "Personalised plan based on your work hours",
            "Skip, snooze or replay any session",
            "Calm typography, almost zero notifications",
          ],
        },
        {
          title: "Guided in 60 seconds",
          description:
            "Slow, illustrated cues with breathing prompts. Pause, skip, or replay — your body, your pace.",
          bullets: [
            "45–90 second guided micro-exercises",
            "Targeted to neck, back and wrists",
            "Voice-free option for open-plan offices",
          ],
        },
        {
          title: "A weekly rhythm",
          description:
            "See the days you moved, the spots that needed extra care, and the streaks that quietly built a habit.",
          bullets: [
            "Tracks your desk hours and pairs them with sessions",
            "Highlights the times your body tightens up most",
            "Gentle streaks — never punishing",
          ],
        },
      ],
    },
    testimonials: {
      kicker: "Loved by people who sit too long",
      h2: "The kind of app you actually keep on your home screen.",
      badgeTitle: "Designed with health professionals",
      badgeNote: "Reviewed by physiotherapists and ergonomic specialists",
      items: [
        {
          name: "Maya R.",
          role: "Senior Product Designer",
          quote:
            "I used to end every Friday with a stiff neck. Three weeks in and that's just… gone. The reminders feel kind, not annoying.",
        },
        {
          name: "Daniel K.",
          role: "Backend Engineer",
          quote:
            "60 seconds is the only window I have between standups. Health Break is the first wellness app I haven't deleted in week two.",
        },
        {
          name: "Aïcha B.",
          role: "Operations Lead",
          quote:
            "It feels like a calm coworker tapping me on the shoulder. My lower back hasn't thanked me this much in years.",
        },
      ],
    },
    faq: {
      kicker: "FAQ",
      h2: "Quick answers, in plain language.",
      items: [
        {
          q: "How long does an exercise take?",
          a: "Around 60 seconds. Each guided break is between 45 and 90 seconds — short enough to do without leaving your chair, long enough to feel a real change.",
        },
        {
          q: "How often will Health Break interrupt me?",
          a: "Only at the right moments. Health Break learns your work hours and focus blocks, then suggests a 1-minute break a few times across the day — never during deep work.",
        },
        {
          q: "Can I customise reminders to my schedule?",
          a: "Absolutely. Set your work hours, focus blocks, and lunch break. Health Break only nudges you at moments that won't break your flow.",
        },
        {
          q: "How do I sign up?",
          a: "Sign in with Apple, Google, or email + password — whichever you prefer. Onboarding takes about 30 seconds and you can change everything later.",
        },
        {
          q: "Do I need any equipment?",
          a: "Nope. Every exercise is designed for a desk, a chair, and a small bit of space. No mat, no resistance bands, no gym.",
        },
        {
          q: "Is it suitable for beginners?",
          a: "Yes — it's built for people who haven't stretched in months. Movements are slow, optional, and explained clearly. Skip anything that doesn't feel right for your body.",
        },
        {
          q: "Is it on iOS and Android?",
          a: "Yes. Health Break is available on both iOS and Android, so you can download it from the App Store or Google Play.",
        },
      ],
    },
    cta: {
      kicker: "Ready when you are",
      h2Pre: "Give your body",
      h2Highlight: "one minute.",
      h2Post: "Get the rest of your day back.",
      lead: "Join the early-access waitlist or download Health Break the moment it lands on your store.",
      appStoreSmall: "Download on",
      appStoreBig: "App Store",
      googleSmall: "Get it on",
      googleBig: "Google Play",
      orWaitlist: "Or join the waitlist",
      success: "You’re in. We’ll email when early access opens.",
      placeholder: "you@calmwork.com",
      submit: "Join waitlist",
      submitLoading: "Adding…",
      privacyNote:
        "No spam. One email when early access opens. Unsubscribe anytime.",
      stickyTitle: "One minute. Big difference.",
      stickyCta: "Get app",
      toastError:
        "Couldn't add you right now. Try again in a moment.",
    },
    footer: {
      productKicker: "Product",
      companyKicker: "Company",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      contact: "Contact",
      tagline:
        "One-minute breaks built for people who work at a desk all day. Move gently, stay loose, feel better at 5pm.",
      copyright: "All rights reserved.",
      madeBy: "Made by",
      madeByCompany: "Venei Software LLC",
      madeWithLove: "with care for people who sit too much.",
    },
    legal: {
      back: "Back to home",
      lastUpdated: "Last updated",
    },
    privacy: {
      kicker: "Privacy",
      title: "Privacy Policy",
      lastUpdated: "December 1, 2025",
    },
    terms: {
      kicker: "Terms",
      title: "Terms of Use",
      lastUpdated: "December 1, 2025",
    },
  },

  // ────────────── TÜRKÇE ──────────────
  tr: {
    brand: {
      name: "Health Break",
      kicker: "Hareket · Mola · Tekrar",
      tagline: "Tek seferde bir dakika.",
    },
    nav: {
      features: "Özellikler",
      how: "Nasıl çalışır",
      preview: "Uygulama",
      reviews: "Yorumlar",
      faq: "SSS",
      cta: "Uygulamayı indir",
      menuOpen: "Menüyü aç",
      menuClose: "Menüyü kapat",
      lightAria: "Açık moda geç",
      darkAria: "Koyu moda geç",
    },
    hero: {
      eyebrow: "Masa başı için tasarlandı, salon için değil",
      h1Pre: "Aralıklı 1 dakikalık egzersizler ile",
      h1Highlight: "sağlıklı bir beden.",
      h1Post: "",
      sub: "Health Break, uzun süre masa başında oturmaya bağlı oluşabilecek rahatsızlıkları azaltmayı desteklemek için tasarlanmış 1 dakikalık egzersiz molaları sunar. Uzmanlar tarafından hazırlanan kısa hareketlerle boyun, sırt, omuz, göz ve bilek bölgelerini masadan kalkmadan gün içinde hareketlendirmene yardımcı olur. Akıllı hatırlatmalar sayesinde çalışma akışını bozmadan düzenli mola alışkanlığı kazandırır.",
      ctaPrimary: "Uygulamayı indir",
      ctaSecondary: "Nasıl çalıştığını gör",
      trust: "Fizyoterapistlerle birlikte tasarlandı. Tüm gün masa başında çalışan herkes için.",
      shotAlt: "Health Break — Boyun döndürme egzersizi ekranı",
    },
    problem: {
      kicker: "Masa başı sorunu",
      h2Pre: "Oturmak bu kadar",
      h2Highlight: "ağır olmamalıydı.",
      lead: "Çoğu masa başı çalışan günde 8+ saatini yanlış pozisyonda geçiriyor. Beden gün boyu küçük hareketlere ihtiyaç duyuyor ancak çoğumuz bir yerimiz ağrıdığında bedenimizi duyuyoruz.",
      withoutTitle: "Molasız geçen günler",
      withTitle: "Health Break ile",
      withoutItems: [
        "Gün boyu gergin boyun ve omuzlar",
        "Geçmeyen bel ağrısı",
        "Uzun yazma seanslarından sonra zonklayan bilekler",
        "Gün içinde artan göz yorgunluğu",
      ],
      withItems: [
        "1 dakikalık kısa ve sağlıklı molalar",
        "Ağrı noktalarına özel mikro egzersizler",
        "Odağına saygı duyan hatırlatmalar",
        "Gerçek bir alışkanlığa dönüşen sağlıklı bir ritim",
      ],
    },
    features: {
      kicker: "İçinde neler var",
      h2Pre: "Uzun oturma saatleri",
      h2Highlight: "bedeninde birikmesin.",
      lead: "Çalışma akışını bozmadan, bedenine küçük ama düzenli molalar kazandıran özellikler",
      items: [
        {
          id: "one-minute",
          title: "Gün içinde 1 dakikalık molalar",
          description:
            "Doğru anlarda devreye giren kısa rehberli rutinler — toplantılar arasında, uzun odak bloklarından sonra, o ünlü öğleden sonra düşüşünden önce.",
          icon: "Timer",
          span: "md:col-span-7",
          accent: "emerald",
        },
        {
          id: "targeted-areas",
          title: "Masa başı ağrılarına özel",
          description:
            "Boyun, sırt, göz, bel ve bilekler: Bedeninin en çok zorladığı yerler.",
          icon: "PersonSimpleTaiChi",
          span: "md:col-span-5",
          accent: "warm",
        },
        {
          id: "smart-reminders",
          title: "Gününe saygı duyan hatırlatmalar",
          description:
            "Gerçek çalışma saatlerine ve odak bloklarına göre ayarlanır. Derin odak sırasında sessiz, beden mola istediğinde nazik.",
          icon: "BellSimpleRinging",
          span: "md:col-span-5",
          accent: "warm",
        },
        {
          id: "weekly-rhythm",
          title: "Masa süresi + hareket, tek bir ritimde",
          description:
            "Ne kadar süredir çalıştığını sessizce takip eder, seanslarınla birleştirir ve sakin bir haftalık tablo kendiliğinden oluşur.",
          icon: "ChartLineUp",
          span: "md:col-span-7",
          accent: "emerald",
        },
        {
          id: "friendly-onboarding",
          title: "Hemen başla, bedenine göre ilerle",
          description:
            "Birkaç soruyla çalışma düzenini ve hedef bölgelerini tanıyalım; egzersiz molalarını sana uygun hale getirelim.",
          icon: "SignIn",
          span: "md:col-span-6",
          accent: "neutral",
        },
        {
          id: "habit-insights",
          title: "Küçük molalar rutine dönüşsün",
          description:
            "Günlük hedefler, seri takibi ve akıllı hatırlatmalarla 1 dakikalık egzersizleri çalışma gününün doğal parçası haline getir.",
          icon: "Medal",
          span: "md:col-span-6",
          accent: "neutral",
        },
      ],
    },
    how: {
      kicker: "Nasıl çalışır",
      h2Pre: "Uzun oturma saatlerini",
      h2Highlight: "küçük molalara böl.",
      stepLabel: "Adım",
      steps: [
        {
          number: "01",
          title: "İhtiyacını belirle",
          description:
            "Çalışma saatlerini ve en çok zorlanan bölgelerini seç. Health Break molalarını sana göre düzenlesin.",
        },
        {
          number: "02",
          title: "Doğru zamanda hatırla",
          description:
            "Akıllı hatırlatmalar, çalışma akışını bozmadan kısa egzersiz molaları vermene yardımcı olur.",
        },
        {
          number: "03",
          title: "60 saniye hareket et",
          description:
            "Uzmanlar tarafından hazırlanan rehberli egzersizleri masadan kalkmadan uygula.",
        },
      ],
    },
    preview: {
      kicker: "Uygulamanın içinde",
      h2: "En yoğun gününde bile gizli, sakin bir alan.",
      lead: "Health Break, ihtiyaç duyduğunda yanında olan; sade arayüzü, anlaşılır egzersizleri ve çalışma akışını bölmeyen hatırlatmalarıyla tasarlandı.",
      authTitle: "Sana uygun girişi seç",
      authNote:
        "Zaten güvendiğin seçenekle devam et. Ayarlardan istediğin zaman değiştirebilirsin.",
      authApple: "Apple",
      authGoogle: "Google",
      authEmail: "E-posta",
      highlights: [
        {
          title: "Günlük mola planı",
          description:
            "Çalışma saatlerini ve en çok zorlanan bölgelerini seç. Health Break, gün içine sana uygun kısa egzersiz molaları yerleştirsin.",
          bullets: [
            "Çalışma saatlerine göre kişisel plan",
            "Boyun, sırt, omuz, göz ve bilek odakları",
            "Seansları atla, ertele veya tekrar et",
          ],
        },
        {
          title: "60 saniyelik rehberli egzersizler",
          description:
            "Uzmanlar tarafından hazırlanan kısa hareketleri adım adım takip et. Her egzersiz, masadan kalkmadan uygulanabilecek şekilde tasarlandı.",
          bullets: [
            "1 dakikalık mikro egzersizler",
            "Net ve kolay takip edilen yönergeler",
            "Masa başında uygulanabilir hareketler",
          ],
        },
        {
          title: "Haftalık ilerleme",
          description:
            "Hangi günlerde mola verdiğini, hangi bölgeleri çalıştırdığını ve düzenini nasıl koruduğunu kolayca takip et.",
          bullets: [
            "Hareket ettiğin günleri gör",
            "En çok odaklandığın bölgeleri takip et",
            "Baskı yaratmayan alışkanlık takibi",
          ],
        },
      ],
    },
    testimonials: {
      kicker: "MASA BAŞI ÇALIŞANLAR TARAFINDAN SEVİLDİ",
      h2: "Gerçekten ana ekranında tutacağın türden bir uygulama.",
      badgeTitle: "Sağlık profesyonelleriyle tasarlandı",
      badgeNote: "Fizyoterapistler ve ergonomi uzmanları tarafından incelendi",
      items: [
        {
          name: "Maya R.",
          role: "Kıdemli Ürün Tasarımcısı",
          quote:
            "Eskiden her cuma boynum tutulmuş şekilde biterdi. Üç hafta oldu, o his tamamen gitti. Hatırlatmalar nazik, sıkıcı değil.",
        },
        {
          name: "Daniel K.",
          role: "Backend Mühendisi",
          quote:
            "Standup'lar arasında 60 saniyem var. Health Break, ikinci hafta silmediğim ilk wellness uygulaması.",
        },
        {
          name: "Aïcha B.",
          role: "Operasyon Lideri",
          quote:
            "Sakin bir iş arkadaşının omzuma dokunması gibi hissettiriyor. Belim yıllardır bana bu kadar teşekkür etmemişti.",
        },
      ],
    },
    faq: {
      kicker: "SSS",
      h2: "Kısa ve sade cevaplar.",
      items: [
        {
          q: "Bir egzersiz ne kadar sürer?",
          a: "Yaklaşık 60 saniye. Her rehberli mola 45 ila 90 saniye arası — sandalyenden kalkmadan yapacak kadar kısa, gerçek bir fark hissedecek kadar uzun.",
        },
        {
          q: "Health Break beni ne sıklıkta böler?",
          a: "Sadece doğru anlarda. Health Break çalışma saatlerini ve odak bloklarını öğrenir, gün içinde birkaç kez 1 dakikalık mola önerir — derin odak sırasında asla.",
        },
        {
          q: "Hatırlatmaları kendi programıma göre özelleştirebilir miyim?",
          a: "Kesinlikle. Çalışma saatlerini, odak bloklarını ve öğle molanı ayarla. Health Break sadece akışını bozmayacak anlarda nazikçe dürter.",
        },
        {
          q: "Nasıl üye olurum?",
          a: "Apple, Google veya e-posta + şifre ile giriş yap — hangisi senin için uygunsa. Kurulum yaklaşık 30 saniye sürer ve her şeyi sonradan değiştirebilirsin.",
        },
        {
          q: "Herhangi bir ekipmana ihtiyacım var mı?",
          a: "Hayır. Her egzersiz bir masa, bir sandalye ve küçük bir alan için tasarlandı. Mat yok, direnç bandı yok, salon yok.",
        },
        {
          q: "Yeni başlayanlar için uygun mu?",
          a: "Evet — aylardır esneme yapmamış kişiler için tasarlandı. Hareketler yavaş, opsiyonel ve net biçimde anlatılıyor. Vücuduna uygun gelmeyen herhangi bir hareketi atlayabilirsin.",
        },
        {
          q: "iOS ve Android'de var mı?",
          a: "Evet. Health Break hem iOS hem Android'de var; App Store veya Google Play'den indirebilirsin.",
        },
      ],
    },
    cta: {
      kicker: "Hazır olduğunda başla",
      h2Pre: "Bedenine",
      h2Highlight: "bir dakika.",
      h2Post: "Günün geri kalanını geri al.",
      lead: "Erken erişim listesine katıl ya da Health Break mağazana ulaştığı an hemen indir.",
      appStoreSmall: "Şuradan İndir",
      appStoreBig: "App Store",
      googleSmall: "Şuradan İndir",
      googleBig: "Google Play",
      orWaitlist: "Veya bekleme listesine katıl",
      success: "Listedesin. Erken erişim açıldığında e-posta atacağız.",
      placeholder: "sen@huzurluis.com",
      submit: "Listeye katıl",
      submitLoading: "Ekleniyor…",
      privacyNote:
        "Spam yok. Erken erişim açıldığında tek bir e-posta. Dilediğin an çıkabilirsin.",
      stickyTitle: "Bir dakika. Büyük fark.",
      stickyCta: "Uygulamayı al",
      toastError:
        "Şu an seni ekleyemedik. Birazdan tekrar dener misin?",
    },
    footer: {
      productKicker: "Ürün",
      companyKicker: "Şirket",
      privacy: "Gizlilik Politikası",
      terms: "Kullanım Koşulları",
      contact: "İletişim",
      tagline:
        "Tüm gün masa başında çalışanlar için tasarlanmış 1 dakikalık molalar. Nazikçe hareket et, gevşek kal, akşam 5'te kendini iyi hisset.",
      copyright: "Tüm hakları saklıdır.",
      madeBy: "Hazırlayan",
      madeByCompany: "Venei Software LLC",
      madeWithLove: "çok oturan insanlar için özenle.",
    },
    legal: {
      back: "Ana sayfaya dön",
      lastUpdated: "Son güncelleme",
    },
    privacy: {
      kicker: "Gizlilik",
      title: "Gizlilik Politikası",
      lastUpdated: "1 Aralık 2025",
    },
    terms: {
      kicker: "Koşullar",
      title: "Kullanım Koşulları",
      lastUpdated: "1 Aralık 2025",
    },
  },
};

export const SOCIALS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/health-break-app",
    icon: "linkedin",
    testid: "footer-social-linkedin",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/healthbreak.app/",
    icon: "instagram",
    testid: "footer-social-instagram",
  },
];

export const CONTACT_EMAIL = "info@venei.co";

export const BRAND_LOGO_URL =
  "https://helpimal-images.s3.eu-central-1.amazonaws.com/hb-assets/AppIcon-1024.png";

export const HERO_SCREENSHOT_URL =
  "https://helpimal-images.s3.eu-central-1.amazonaws.com/hb-assets/Simulator+Screenshot+-+iPhone+17+-+2026-05-12+at+15.59.57.png";

export const TESTIMONIAL_AVATARS = [
  "https://images.unsplash.com/photo-1769636929388-99eff95d3bf1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3ODIxODg3OHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1672685667592-0392f458f46f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3ODIxODg3OHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3ODIxODg3OHww&ixlib=rb-4.1.0&q=85",
];

export const NAV_HASHES = [
  { key: "features", href: "/#features" },
  { key: "how", href: "/#how-it-works" },
  { key: "preview", href: "/#app-preview" },
  { key: "reviews", href: "/#testimonials" },
  { key: "faq", href: "/#faq" },
];
